import { gsap } from "gsap"
import { Flip } from "gsap/Flip"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import settings from "@/scripts/common/settings"
import { utilities } from "@/scripts/common/utilities"
import mapStyles from './map.style'
import GMap from "./GMap"
import Ripple from "../ripple"

import mainTpl from "./templates/main.template"
import singleTpl from "./templates/hotel-single.template"
import modalTpl from "./templates/map-modal.template"

import { API } from '@/scripts/common/api/api.services'

gsap.registerPlugin(Flip)
gsap.registerPlugin(ScrollToPlugin)
gsap.registerPlugin(ScrollTrigger)

const CLASS_PRE = 'hotels'
const CLASS_PRE_HOTSPOTS = 'hotspots'

const APIServices = new API.Controller()

export const Maps = (() => {
    class HotelFinder {
        constructor(container) {
            this.$el = container
            this.$container = null
            this.$toolbar = null
            this.$filter = null
            this.$header = null
            this.$body = null
            this.$map = null
            this.$mapContainer = null
            this.$title = null
            this.$mobileBackLinkText = null
            this.$hotelList = null
            this.$hotelSingle = null
            this.$hotelSingleDetail = null
            this.$hotelSingleBackLink = null

            this.isMobile = window.innerWidth < settings.breakpoints.mobile

            this.data = this.$el.dataset.value
            this.options = Object.assign({}, defaultHotelFinderOptions.others)
            this.filterItems = [
                ...defaultHotelFinderOptions.filter
            ]
            this.hotelsLoading = false

            this.hotelCat = this.filterItems[0].children[0].value // default first cat value
            this.hotelCatChild = ''

            this.grid = 'list'
            this.hotelListToggleClass = `${CLASS_PRE}__list--compact`
            this.hotelListTweeingClass = `${CLASS_PRE}__list--tweening`
            this.hotelItemClass = `hotel-item`
            this.hotelStateListClass = 'state-list'
            this.hotelList = []
            this.locSingleDetail = null
            this.showSingle = false
            this.singleContentWidthPercent = window.innerWidth > 1024 ? 35 : 100

            // Timelines and states
            this.flipGridStates = null

            // Google maps
            this.Map = null
            this.googleMapLoaderOptions = {}



            this.init()
        }

        init() {
            this.selectChildCat()
            this.fetchItems()
            this.setupHtml()
            this.setupMap()
            this.initAnimations()
            this.updateHtmls()
            this.addListener()
        }

        setupHtml() {
            this._createContainer()
            if (this.isMobile) this._createToolBar()
            this._createFilters()
            this._createSingleHotelArticle()
            this.$container && this.$el.appendChild(this.$container)
            this.assignElems()
            this.$toolbar && this.$header.appendChild(this.$toolbar)
            this.$filter && this.$header.appendChild(this.$filter)
            this.$hotelSingleDetail && this.$hotelSingle.appendChild(this.$hotelSingleDetail)
        }

        setupMap() {
            this.clearMap()
            this.$mapContainer.appendChild(this._createMap())
            gsap.set(this.$mapContainer, { display: 'none', width: 0, autoAlpha: 0 })
            gsap.set(this.$map, { autoAlpha: 0 })
            this.initGoogleMap() // calling google maps
        }

        initAnimations() {
            if (!this.isMobile) {
                gsap.set(this.$hotelSingle, { xPercent: -100 })
            } else {
                gsap.set(this.$hotelSingle, { yPercent: 100 })
            }

        }

        assignElems() {
            this.$header = this.$header || this.$container.querySelector(`.${CLASS_PRE}__header`)
            this.$body = this.$body || this.$container.querySelector(`.${CLASS_PRE}__body`)
            this.$mapContainer = this.$mapContainer || this.$container.querySelector(`.${CLASS_PRE}__map`)
            this.$title = this.$title || this.$container.querySelector(`.${CLASS_PRE}__title`)
            this.$hotelList = this.$hotelList || this.$container.querySelector(`.${CLASS_PRE}__list`)
            this.$hotelSingle = this.$hotelSingle || this.$container.querySelector(`.${CLASS_PRE}__single`)
        }



        // Fethcing data
        async fetchItems() {

            if (!APIServices.isJson(this.data)) {
                this.hotelsLoading = true
                const data = await APIServices.getFetch(this.data)
                this.parseData(data)
                this._createList()
                this.hotelsLoading = false
                return
            }

            this.parseData(JSON.parse(this.data))
            this._createList()
        }
        // Parsing recieved data
        parseData(items) {
            const dataItems = []
            items.forEach((item, i) => {
                const country = item.country
                const hotels = item.hotels
                const states = []
                hotels.forEach((hotel, i) => {
                    if (states.findIndex(state => state.name === hotel.state) === -1) {  // Adding states
                        states.push(Object.assign({
                            name: hotel.state,
                            hotels: hotels.filter(h => h.state === hotel.state), // Filtering hotels by the state and adding
                        },
                            ...this.filterItems[0].children.map((filter) => {
                                const obj = {}
                                if(filter.children) {
                                    Object.assign(obj,
                                        ...filter.children.map((childFilter) => {
                                            const childFilterObj = {}
                                            childFilterObj[childFilter.value] = hotels.filter(h => h.state === hotel.state && h[`${filter.value}Type`] === childFilter.value).length
                                            return childFilterObj
                                        })
                                    )
                                } 
                                obj[filter.value] = hotels.filter(h => h.state === hotel.state && h.type === filter.value).length
                                return obj
                            })
                        ))
                    }
                })

               


                dataItems.push(Object.assign({
                    name: country,
                    states
                },
                    ...this.filterItems[0].children.map((filter) => {
                        const obj = {}
                        if(filter.children) {
                            Object.assign(obj,
                                ...filter.children.map((childFilter) => {
                                    const childFilterObj = {}
                                    childFilterObj[childFilter.value] = hotels.filter((h) => h[`${filter.value}Type`] === childFilter.value).length
                                    return childFilterObj
                                })
                            )
                        } 
                        obj[filter.value] = hotels.filter(h => h.type  === filter.value).length
                        return obj
                    })
                ))
            })

            this.hotelList = dataItems
        }

        // Google maps

        async initGoogleMap() {
            this.Map = null
            this.Map = new GMap(this.$map, this.googleMapLoaderOptions)
            const google = await this.Map.loadMap()
            this.Map.setMap(google, {
                center: { lat: 41.8719, lng: 12.5674 },
                zoom: 7,
                styles: mapStyles
            })


            this.updateMarkers()
        }

        updateMarkers() {
            if (!this.Map) return
            this.Map.removeAllMarkers()
            const countryies = this.hotelList
            countryies.forEach((country) => {
                const states = country.states
                states.forEach(state => {
                    const hotels = state.hotels
                    hotels.forEach((hotel) => {
                        if (hotel.type === this.hotelCat) {
                            this.Map.addMarker(`${hotel.latitude} ${hotel.longitude}`, {
                                ...hotel
                            })
                        }
                    })
                })
            })
        }


        // Clears and resets
        cleanHotelListItems() {
            const $items = this.$hotelList.querySelectorAll(`.${this.hotelItemClass}`)
            $items.forEach(item => item.remove())
        }

        clearMap() {
            this.$map && this.$map.remove()
        }

        // Html cooking and preperation
        // Container main
        _createContainer() {
            const parallelList = this.$el.querySelector('.hotel-parallel-list')
            if (parallelList) parallelList.remove() // Removing SEO element
            document.querySelector(`.${CLASS_PRE}__container`) && document.querySelector(`.${CLASS_PRE}__container`).remove()
            const container = document.createElement('div')
            container.classList.add(`${CLASS_PRE}__container`)
            container.innerHTML = mainTpl()
            this.$container = container
        }

        // Toolbars for mobile
        _createToolBar() {
            const toolBar = document.createElement('div')
            toolBar.classList.add(`${CLASS_PRE}__toolbar`)
            const backLink = document.createElement('span')
            backLink.classList.add(`${CLASS_PRE}__back-link`)
            backLink.setAttribute('data-ripple', '')
            backLink.innerHTML = `<svg class="icon-svg icon-svg--outline mr-2"><use xlink:href="#icon-angle-left"></use></svg> <span class="${CLASS_PRE}__back-link-text h4 mb-0">BACK</span>`
            backLink.addEventListener("click", () => this.callMainMenu())
            new Ripple(backLink)

            this.$mobileBackLinkText = backLink.querySelector(`.${CLASS_PRE}__back-link-text`)

            toolBar.appendChild(backLink)
            this.$toolbar = toolBar
        }

        // Filters section
        _createFilters() {
            const filterDiv = document.createElement('div')
            filterDiv.classList.add(`${CLASS_PRE}__filter`)

            this.filterItems.forEach((item, i) => {
                const list = document.createElement('ul')
                list.classList.add(`${CLASS_PRE}__filter-list`)
                list.dataset.filter = item.name
                item.children.forEach(filter => {
                    list.appendChild(this._createFilterItem(filter, item.name))
                })

                i && filterDiv.appendChild(this._createFilterSeparator())

                filterDiv.appendChild(list)
            })

            this.$filter = filterDiv
        }

        _createFilterItem(filter, cat) {
            const filterItem = document.createElement('li')
            filterItem.classList.add(`${CLASS_PRE}__filter-item`)
            const filterTrigger = document.createElement('span')
            filterTrigger.classList.add(`${CLASS_PRE}__filter-trigger`)
            filterTrigger.dataset.value = filter.value
            filterTrigger.innerHTML = `${filter.title}`
            filterTrigger.addEventListener('click', () => this.filterSelect(filter.value, cat))

            filterTrigger.setAttribute('data-ripple', '')
            new Ripple(filterTrigger)

            filterItem.appendChild(filterTrigger)
            return filterItem
        }

        _createFilterSeparator() {
            const seperator = document.createElement('span')
            seperator.classList.add(`${CLASS_PRE}__filter-seperator`)
            return seperator
        }

        // Creating list
        _createList() {
            const cat = this.hotelCat
            const childCat = this.hotelCatChild

            this.cleanHotelListItems()
            this.hotelList.forEach((hotel) => {
                if(childCat) {
                    if(hotel[cat] !== 0 && hotel[childCat] !== 0) this.$hotelList.appendChild(this._createItemList(hotel, this.hotelItemClass))
                    return
                }
                if (hotel[cat] !== 0) this.$hotelList.appendChild(this._createItemList(hotel, this.hotelItemClass))
            })
            this.updateHtmls()
        }

        // Building Hotel list
        _createItemList(listItem, classPre) {
            const item = document.createElement('div')
            const children = listItem.states || listItem.hotels || []
            item.classList.add(`${classPre}`)
            item.innerHTML = listItem.hotels ? `<h5 class="${this.hotelStateListClass}__name">${listItem.name}</h5>` : `<h2 class="${classPre}__country">${listItem.name}</h2>`

            if (children.length) {
                const childList = document.createElement('div')
                const childClass = listItem.hotels ? `${this.hotelStateListClass}__hotels` : `${this.hotelStateListClass}`
                childList.classList.add(`${childClass}`)
                children.forEach(child => {
                    if (child.hotels) {
                        if(this.hotelCatChild) {
                            if (child[this.hotelCat] !== 0 && child[this.hotelCatChild] !== 0) childList.appendChild(this._createItemList(child, `${this.hotelStateListClass}__item`)) // recursive calling for state list
                            return
                        }
                        if (child[this.hotelCat] !== 0) childList.appendChild(this._createItemList(child, `${this.hotelStateListClass}__item`)) // recursive calling for state list
                        return
                    }
                    
                    if(this.hotelCatChild) {
                        (child.type === this.hotelCat) && (child[`${child.type}Type`] === this.hotelCatChild) && childList.appendChild(this._createHotelItem(child)) // For hotel list
                        return
                    }
                    (child.type === this.hotelCat) && childList.appendChild(this._createHotelItem(child)) // For hotel list
                })
                item.appendChild(childList)
            }
            return item
        }

        // Hotel list Item
        _createHotelItem(hotel) {
            const hotelItem = document.createElement('div')
            hotelItem.classList.add(`${this.hotelStateListClass}__hotels-item`)
            hotelItem.detail = {
                ...hotel
            }
            hotelItem.innerHTML = `<span class="${this.hotelStateListClass}__hotel-name">${hotel.name}<svg class="icon-svg icon-svg--outline ml-1">
                <use class="add" xlink:href="#icon-add"></use>
                <use class="minus" xlink:href="#icon-minus"></use>
            </svg></span>`
            hotelItem.setAttribute('data-ripple', '')
            new Ripple(hotelItem)

            if (this.isMobile) { // Creating accordion content for mobiles
                const class_pre = "hotel-detail"
                const $accordianDetail = this._createAccordianDetail()
                const $accordianDetailImg = $accordianDetail.querySelector(`.${class_pre}__featured-image`)
                const $address = $accordianDetail.querySelector(`.${class_pre}__address`)
                const $description = $accordianDetail.querySelector(`.${class_pre}__description`)
                const $link = $accordianDetail.querySelector(`.${class_pre}__link`)
                $accordianDetailImg.setAttribute('data-image', `${hotelItem.detail["featuredImage"]}`)
                $address.innerHTML = `${hotelItem.detail.address}`
                $description.innerHTML = `${hotelItem.detail.description}`
                $link.setAttribute('href', `${hotelItem.detail.link}`)
                hotelItem.appendChild($accordianDetail)
            }

            hotelItem.querySelector(`.${this.hotelStateListClass}__hotel-name`).addEventListener("click", () => {
                this.selectLocation(hotelItem.detail, this.isMobile ? true : false)
                if (this.isMobile) this.accordianToggle(hotelItem)
            }) // Giving hotelitem click listener
            return hotelItem
        }

        // Create single list item
        _createSingleHotelArticle() {
            const hotelDetail = utilities.createNodeParsing(singleTpl())
            this.$hotelSingleDetail = hotelDetail
            this.$hotelSingleBackLink = hotelDetail.querySelector('.hotel-detail__back-link')
            this.$hotelSingleBackLink.setAttribute('data-ripple', '')
            new Ripple(this.$hotelSingleBackLink)

            this.$hotelSingleBackLink.addEventListener("click", () => this.showSingleSheet(false))
        }

        // Accordian single component
        _createAccordianDetail() {
            const hotelDetail = utilities.createNodeParsing(singleTpl())
            hotelDetail.querySelector('.hotel-detail__header').remove()
            hotelDetail.querySelector('.hotel-detail__title').remove()
            return hotelDetail
        }

        // Create map
        _createMap() {
            const map = document.createElement("div")
            map.setAttribute('id', 'hotels-map')
            map.classList.add('hotels-map')
            gsap.set(map, {
                width: "100%",
                height: "100%",
                position: "absolute",
                right: 0,
                top: 0,
                background: 'blue'
            })

            map.innerHTML = '<h1>Map</h1>'

            this.$map = map

            return map
        }


        // Getting prev states of the hotel item
        getListItemPrevStates() {
            this.flipGridStates = null
            this.flipGridStates = Flip.getState([this.$hotelList, this.$hotelList.querySelectorAll('.hotel-item')])
        }

        // Animations Cooking and triggers
        flipGrids() {
            const mapGrid = this.grid === "map"
            const listGrid = this.grid === "list"
            this.getListItemPrevStates()
            !listGrid ? this.$hotelList.classList.add(`${this.hotelListToggleClass}`) : this.$hotelList.classList.remove(`${this.hotelListToggleClass}`)
            const tl = gsap.timeline({
                onStart: () => this.$hotelList.classList.add(`${this.hotelListTweeingClass}`),
                onComplete: () => this.$hotelList.classList.remove(`${this.hotelListTweeingClass}`)
            })

            this.showSingle && this.listGrid && this.showSingleSheet(false)
            this.isMobile && this.accordionReset()

            if (!this.isMobile)
                tl.add(Flip.from(this.flipGridStates, {
                    absolute: false,
                    duration: this.options.flipAnim ? 0.6 : 0,
                    stagger: 0.05,
                    nested: true,
                    ease: 'power2.inOut',
                    //onLeave: (e) => gsap.to(e, {duration: 1, autoAlpha: 0}),
                    //onEnter: (e) => gsap.to(e, {duration: 1, autoAlpha: 1}),
                }), 'gridswitch')

            if (mapGrid) {
                tl.add(this.mapReveal(true), 'gridswitch')
            } else if (listGrid) {
                tl.add(this.mapReveal(false), 'gridswitch')
            }

        }

        singleDetailsToggle() {
            const $listContainer = this.$hotelList
            const $mapContainer = this.$mapContainer
            const $singleDetail = this.$hotelSingle
            const ease = 'power2.inOut'

            const tl = gsap.timeline()
            if (!this.isMobile) {
                tl.to($singleDetail, {
                    duration: 0.4,
                    xPercent: this.showSingle ? 0 : -100,
                    ease
                })
            } else {
                tl.to($singleDetail, {
                    duration: 0.4,
                    x: 0,
                    yPercent: this.showSingle ? 0 : 100,
                    ease
                })
                    .to($mapContainer, {
                        duration: 0.4,
                        height: this.showSingle ? '50%' : '100%',
                        ease
                    }, 0)
                    .to($listContainer, {
                        duration: 0.4,
                        maxHeight: this.showSingle ? '40%' : '100%',
                        ease
                    }, 0)
            }

            return tl
        }

        mapReveal(show) {
            const mapWidth = this.gridSizeCalc().mapWidth
            gsap.set(this.$map, { width: mapWidth })
            const tl = gsap.timeline()
                .to(this.$mapContainer, {
                    autoAlpha: 1,
                    duration: 0.6,
                    width: show ? mapWidth : 0,
                    transformOrigin: '100% 50%',
                    display: show ? 'block' : 'none',
                    overwrite: 'all',
                    ease: 'power2.inOut'
                })
                .to(this.$map, {
                    autoAlpha: show ? 1 : 0,
                    duration: 0.4,
                    ease: 'power2.inOut',
                    overwrite: 'all',
                }, show ? 0 : 0.2)


            return tl
        }

        // GridWidthCalc
        gridSizeCalc() {
            const gridSize = {
                listWidth: 0,
                mapWidth: 0
            }
            gridSize.mapWidth = this.$body.getBoundingClientRect().width - (this.$body.getBoundingClientRect().width * (this.singleContentWidthPercent / 100)) || this.$body.getBoundingClientRect().width
            gridSize.listWidth = (this.$body.getBoundingClientRect().width * (this.singleContentWidthPercent / 100))

            //if(this.isMobile) gridSize.mapWidth = window.innerWidth

            return gridSize
        }

        // Single detail featured image loading
        async featuredImage($imagePanel, src) {
            const class_pre = "hotel-detail"
            const $image = $imagePanel || this.$hotelSingleDetail.querySelector(`.${class_pre}__featured-image`)
            const imgSrc = src || this.locSingleDetail["featuredImage"]
            $image.classList.remove(`bg-img--loaded`)
            $image.classList.add(`bg-img--preload-small`)
            let imageLoaded = ''
            try {
                imageLoaded = await utilities.loadImage(imgSrc)
            } catch (err) {
                imageLoaded = ''
                $image.style.backgroundImage = `url(${settings.noImageUrl})`
            }

            if (imageLoaded === 'loaded') $image.style.backgroundImage = `url(${imgSrc})`
            $image.classList.add(`bg-img--loaded`)
            $image.classList.remove(`bg-img--preload-small`)

        }

        // Class toggles
        toggleClasses() {
            const filterTriggers = this.$header.querySelectorAll(`.${CLASS_PRE}__filter-trigger`)
            const childFilterTriggers = this.$body.querySelectorAll(`.${CLASS_PRE}__child-filter`)
            filterTriggers.forEach(trigger => {
                trigger.dataset.value === this.hotelCat || trigger.dataset.value === this.grid ?
                    trigger.classList.add(`${CLASS_PRE}__filter-trigger--active`) :
                    trigger.classList.remove(`${CLASS_PRE}__filter-trigger--active`)
            })
            childFilterTriggers.forEach(trigger => {
                trigger.dataset.value === this.hotelCatChild ? trigger.classList.add(`${CLASS_PRE}__child-filter--active`) : trigger.classList.remove(`${CLASS_PRE}__child-filter--active`)
            })
        }

        // Html updates
        updateHtmls() {
            this.updateTitle()
            this.updateSingleDetails()
            this.toggleClasses()
        }

        updateTitle() {
            if (this.$title) {
                const filterItem = this.filterItems[0].children.find(c => c.value === this.hotelCat)
                this.$title.innerHTML = ''
                if(filterItem.children) {
                    this.$title.classList.add('d-block')
                    filterItem.children.forEach((childItem) => {
                        const $trigger = document.createElement('span')
                        $trigger.classList.add(`${CLASS_PRE}__child-filter`)
                        $trigger.setAttribute('data-value', childItem.value)
                        $trigger.innerHTML = `${childItem.title}`
                        $trigger.addEventListener("click", () => this.childFilterSelect(childItem.value))
                        this.$title.appendChild($trigger)
                    })
                } else {
                    this.$title.innerHTML = filterItem.title
                }
            }
            if (this.$mobileBackLinkText) this.$mobileBackLinkText.innerHTML = this.filterItems[0].children.find(c => c.value === this.hotelCat).title
        }

        updateSingleDetails() {
            if (!this.$hotelSingleDetail || !this.locSingleDetail) return
            const class_pre = "hotel-detail"
            const detail = this.locSingleDetail
            const $title = this.$hotelSingleDetail.querySelector(`.${class_pre}__title`)
            const $titleM = this.$hotelSingleDetail.querySelector(`.${class_pre}__header-title`)
            const $body = this.$hotelSingleDetail.querySelector(`.${class_pre}__body`)
            const $address = this.$hotelSingleDetail.querySelector(`.${class_pre}__address`)
            const $image = this.$hotelSingleDetail.querySelector(`.${class_pre}__featured-image`)
            const $description = this.$hotelSingleDetail.querySelector(`.${class_pre}__description`)
            const $link = this.$hotelSingleDetail.querySelector(`.${class_pre}__link`)

            gsap.to($body, {
                duration: 0.3,
                scrollTo: 0
            })

            $title.innerHTML = detail.name || ''
            $titleM.innerHTML = detail.name || ''
            $address.innerHTML = detail.address || ''
            $description.innerHTML = detail.description || ''
            $link.setAttribute('href', `${detail.link}`)
            this.featuredImage()
        }

        // Listeners
        selectLocation(detail, noSingle) {
            const value = detail
            if (!this.isMobile) {
                if (this.grid !== "map") {
                    this.grid = "map"
                    this.flipGrids()
                }
            }

            this.locSingleDetail = value

            this.Map && this.Map.setMapCenter(`${this.locSingleDetail.latitude} ${this.locSingleDetail.longitude}`)
            if (!noSingle) this.showSingleSheet(true)
            this.updateHtmls()
        }

        // Accordions
        accordionReset() {
            const $allHotelItems = this.$hotelList.querySelectorAll(`.${this.hotelStateListClass}__hotels-item`)
            $allHotelItems.forEach($item => {
                $item.classList.remove(`${this.hotelStateListClass}__hotels-item--active`)
                const $panel = $item.querySelector('.hotel-detail')
                this.accordionOpen(false, $panel)
            })
        }
        accordianToggle($listItem) {
            const panelOpen = $listItem.classList.contains(`${this.hotelStateListClass}__hotels-item--active`)
            const $panel = $listItem.querySelector('.hotel-detail')
            const $panelImg = $listItem.querySelector('.hotel-detail__featured-image')

            this.accordionReset()
            if (!panelOpen) {
                $listItem.classList.add(`${this.hotelStateListClass}__hotels-item--active`)
                this.accordionOpen(true, $panel)

                if (!$panelImg.classList.contains('bg-img--loaded')) {
                    this.featuredImage($panelImg, $panelImg.dataset.image)
                }
            } else {
                $listItem.classList.remove(`${this.hotelStateListClass}__hotels-item--active`)
                this.accordionOpen(false, $panel)
            }

        }

        accordionOpen(open, ele) {
            const panel = ele
            gsap.to(panel, {
                duration: 0.3,
                height: open ? 'auto' : 0,
                onComplete: () => {
                    if (open) gsap.to(this.$hotelList, {
                        duration: 0.3,
                        scrollTo: panel.parentNode
                    })
                }
            })
        }

        // cat set
        setCat(val) {
            this.hotelCat = val
            this.selectChildCat()
            this._createList()
            this.showSingleSheet(false)
            this.updateMarkers()
            this.updateHtmls()
        }

        selectChildCat(val) {
            if(val) {
                this.hotelCatChild = val
                return
            }
            let childCat = ''
            const catFilters = this.filterItems.find((fi) => fi.name === "catFilter")
            catFilters.children.forEach((fI) => {
                if(fI.value === this.hotelCat && fI.children?.length ) {
                    childCat = fI.children[0].value
                    return
                }
                childCat = ''
            })

            this.hotelCatChild = childCat
        }

        // Filter click event
        filterSelect(val, cat) {

            switch (cat) {
                case "catFilter":
                    if (this.hotelCat === val) return
                    this.setCat(val)
                    break
                case "gridFilter":
                    if (this.grid === val) return
                    this.grid = val
                    this.updateHtmls() // Update htmls which will update classes for grids
                    this.showSingleSheet(false)
                    this.flipGrids()
                    break
                default:
                    this.hotelCat = null
                    this.grid = null
            }

        }

        childFilterSelect(val) {
            this.selectChildCat(val)
            this._createList()
            this.showSingleSheet(false)
            this.updateMarkers()
            this.updateHtmls()
        }

        // Single Show click
        showSingleSheet(show) {
            this.showSingle = show
            this.showSingle ? this.$hotelList.classList.add(`${CLASS_PRE}__list--hidden`) : this.$hotelList.classList.remove(`${CLASS_PRE}__list--hidden`)
            this.singleDetailsToggle()
        }

        // Main menu call
        callMainMenu() {
            const $mainNav = document.querySelector('#side-nav')
            if ($mainNav) {
                $mainNav.getNav().setDelayOverride(true)
                $mainNav.getNav().open()
                $mainNav.getNav().setDelayOverride(false)
            }
        }

        // Listeners
        addListener() {
            this.$map.addEventListener("markerClick", (e) => this.selectLocation(e.detail.getValue()))

        }

    }


    // Hot spots map
    const CLASS_MODAL_PRE = "map-modal"
    class HotSpots {
        constructor(container) {
            this.$hotspots = container
            this.$mapCanvas = null
            this.$spotDetail = null
            this.$spotDetailClose = null
            this.Map = null
            this.mapLoaderOptions = {}
            this.data = this.$hotspots.dataset.value
            this.dataLoading = false
            this.activeSpot = null
            this.modal = false
            this.isMobile = window.innerWidth < settings.breakpoints.mobile
            this.modalTl = null
            this.options = Object.assign({}, {
                ...defaultHotSpotOptions
            })
            this.scrollController = null
            this.init()
        }

        init() {
            this.scrollController = ScrollTrigger.create({
                start: 'top 80%',
                trigger: this.$hotspots,
                onEnter: () => {
                    !this.$mapCanvas && this.mapSetup()
                }
            })

        }

        async mapSetup() {
            this.createHTML()
            this.makeModalTl()
            await this.fetchData()
            this.initMap()
            this.updateHTMLs()
            this._addEventListeners()
        }

        // Create Htmls

        createHTML() {
            this.createMap()
            this.createSpotDetail()
        }

        createMap() {
            if (this.$mapCanvas) this.$mapCanvas.remove()
            this.$mapCanvas = document.createElement('div')
            this.$mapCanvas.classList.add(`${CLASS_PRE_HOTSPOTS}__map`)
            this.$hotspots.appendChild(this.$mapCanvas)
        }

        createSpotDetail() {
            const spotDetail = utilities.createNodeParsing(modalTpl())
            this.$spotDetail = spotDetail
            this.$spotDetailClose = this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__close`)
            this.$spotDetailClose.setAttribute('data-ripple', '')
            this.$spotDetailClose.setAttribute('data-ripple-center', 'true')
            new Ripple(this.$spotDetailClose)
            this.$hotspots.appendChild(this.$spotDetail)
        }

        // Modal Animation
        resetTween() {
            this.modalTl.kill()
            const $animElems = [this.$spotDetail,
            this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__title`),
            this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__detail`),
            this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__description`),
            this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__featured-image`),
            this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__action`)]

            gsap.set($animElems, { clearProps: "all" });
            this.modalTl = null
        }

        makeModalTl() {
            if (this.modalTl) this.resetTween()
            this.modalTl = new gsap.timeline({ paused: true })
            const $animElems = [this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__title`),
            this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__detail`),
            this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__description`),
            this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__featured-image`),
            this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__action`)]

            this.modalTl.from(this.$spotDetail, {
                duration: 0.5,
                ease: 'power2.inOut',
                autoAlpha: 0,
                //y: 50,
                xPercent: -50,
                left: '50%',
                scale: 0,
                borderRadius: '50%',
                display: 'none'
            })
                .from($animElems, {
                    duration: 0.4,
                    y: 50,
                    stagger: 0.1,
                    autoAlpha: 0,
                    ease: 'power2.inOut'
                }, 0.1)
        }

        // Modal Actions
        openModal() {
            this.modal = true
            this.focusMap()
            this.modalTl.play()
        }

        closeModal() {
            this.modal = false
            this.modalTl.reverse()
        }

        // UI actions
        focusMap() {
            gsap.to(window, {
                duration: 0.3,
                scrollTo: {
                    y: this.$hotspots,
                    offsetY: 150
                }
            })
        }

        // Update Html
        updateHTMLs() {
            this.spotDetailUpdate()
        }

        spotDetailUpdate() {
            if (!this.$spotDetail || !this.activeSpot) return
            const spot = this.activeSpot
            const $title = this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__title`)
            const $headerTitle = this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__header-title`)
            const $detail = this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__detail`)
            const $body = this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__body`)
            const $description = this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__description`)
            const $link = this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__link`)

            $title.innerHTML = spot.name || ''
            $headerTitle.innerHTML = spot.name || ''
            $detail.innerHTML = spot.details || ''
            $description.innerHTML = spot.description || ''
            $link.setAttribute('href', `${spot.link}`)
            this.featuredImage()
        }

        async featuredImage($imagePanel, src) {
            const $image = $imagePanel || this.$spotDetail.querySelector(`.${CLASS_MODAL_PRE}__featured-image`)
            const imgSrc = src || this.activeSpot["image"]
            $image.classList.remove(`bg-img--loaded`)
            $image.classList.add(`bg-img--preload-small`)
            let imageLoaded = ''
            try {
                imageLoaded = await utilities.loadImage(imgSrc)
            } catch (err) {
                imageLoaded = ''
                $image.style.backgroundImage = `url(${settings.noImageUrl})`
            }

            if (imageLoaded === 'loaded') $image.style.backgroundImage = `url(${imgSrc})`
            $image.classList.add(`bg-img--loaded`)
            $image.classList.remove(`bg-img--preload-small`)
        }

        // Map
        async initMap() {

            this.Map = null
            this.Map = new GMap(this.$mapCanvas, this.mapLoaderOptions)
            const google = await this.Map.loadMap()
            this.Map.setMap(google, {
                center: { lat: this.data.latitude, lng: this.data.longitude },
                zoom: 12,
                styles: mapStyles
            })
            this.addMarkers()

            if (!this.isMobile) this.Map.selectMarker(this.Map.getMarkers()[0]) // For displaying initial modal on start
        }

        // Markers
        addMarkers() {
            const hotSpots = this.data.hotspots
            // Main Marker

            this.Map?.addMarker(`${this.data.latitude} ${this.data.longitude}`, {
                latitude: this.data.latitude,
                longitude: this.data.longitude,
                name: this.data.name || this.data.place,
                link: this.data.link,
                description: this.data.description,
                image: this.data.image,
                details: this.data.details,
                pin: this.data.pin
            })
            hotSpots.forEach(spot => {
                this.Map.addMarker(`${spot.latitude} ${spot.longitude}`, {
                    ...spot
                })
            })
        }

        // Fethcing data
        async fetchData() {
            if (!APIServices.isJson(this.data)) {
                this.dataLoading = true
                return APIServices.getFetch(this.data)
                    .then((data) => {
                        this.parseData(data)
                        this.dataLoading = false
                    })
            }
            return this.parseData(JSON.parse(this.data))
        }

        parseData(data) {
            this.data = data
        }

        // Callbacks
        onMarkerClick(e) {
            const value = e.detail.getValue()
            this.activeSpot = value
            this.Map && this.Map.setMapCenter(`${this.activeSpot.latitude} ${this.activeSpot.longitude}`)
            this.updateHTMLs()
            if (!this.modal) this.openModal()
        }

        _responsiveFunc() {
            return utilities.debounce(() => {
                // Resize actions
                this.isMobile = window.innerWidth < settings.breakpoints.mobile
                if (this.modal) this.closeModal()
                this.makeModalTl()
            }, 250)
        }

        _addEventListeners() {
            this.$mapCanvas.addEventListener("markerClick", (e) => this.onMarkerClick(e))
            this.$spotDetailClose.addEventListener("click", () => this.modal && this.closeModal())

            window.addEventListener('resize', this._responsiveFunc())
        }
    }


    const defaultHotelFinderOptions = {
        filter: [{
            name: 'catFilter',
            children: [{
                title: 'Hotel, Resort & Villas',
                value: 'resorts'
            }, {
                title: 'Restaurants ',
                value: 'restaurants',
                // children: [{
                //     title: 'Restuarants',
                //     value: 'restuarants'
                // },{
                //     title: 'Bars',
                //     value: 'bars'
                // }]
            }]
        }, {
            name: 'gridFilter',
            children: [{
                title: 'Map',
                value: 'map'
            }, {
                title: 'List',
                value: 'list'
            }]
        }],

        others: {
            flipAnim: true
        },
        ...window.defaultHotelFinderOptions
    }

    const defaultHotSpotOptions = {

    }

    return {
        HotelFinder,
        HotSpots
    }
})()